export interface PageContext {
  url: string;
  title: string;
  pathname: string;
  elementTree: ElementNode;
}

export interface PageElement {
  // 핵심 식별 정보
  selector?: string; // data-id 속성값 (AI에서 사용)
  description?: string; // data-description 속성값
  tag: string;

  // 액션 관련 (선택적)
  type?: string; // input 타입, button 타입 등
  value?: string; // input/textarea 현재 값
  role?: string; // 접근성 역할 (button, tab 등)
}

export interface ElementNode {
  element: PageElement;
  children: ElementNode[];
}

/**
 * 현재 페이지의 메타데이터와 보이는 요소 트리를 추출합니다.
 */
export const getPageContext = (): PageContext => {
  // 가상의 루트 노드 생성
  const rootElement: PageElement = { tag: 'body' };

  // body의 자식들을 전체 트리로 구축 (필터링 없이)
  const children: ElementNode[] = [];
  const bodyChildren = nodeListToArray(document.body.children);

  bodyChildren.forEach((child) => {
    const fullTree = buildElementTree(child);
    if (fullTree) {
      children.push(fullTree); // 전체 트리 그대로 추가
    }
  });

  const elementTree: ElementNode = {
    element: rootElement,
    children: children,
  };

  return {
    url: window.location.href,
    title: document.title,
    pathname: window.location.pathname,
    elementTree: elementTree,
  };
};

/**
 * DOM 트리를 순회하면서 보이는 모든 요소들의 트리를 구축합니다.
 */
const buildElementTree = (element: Element): ElementNode | null => {
  const dataId = element.getAttribute('data-id');
  const dataDescription = element.getAttribute('data-description') || undefined;
  const role = element.getAttribute('role');
  const rowId = element.getAttribute('row-id');

  let selector: string | undefined = undefined;
  let description: string | undefined = undefined;

  if (dataId) {
    selector = `[data-id="${dataId}"]`;
    description = dataDescription;
  }
  if (role == 'row' && rowId !== null) {
    selector = `[row-id="${rowId}"]`;
    description = `rowId: ${rowId}}`;
  }

  const pageElement: PageElement = {
    selector: selector,
    tag: element.tagName.toLowerCase(),
    description: description,
    type: element.getAttribute('type') || undefined,
    value: (element as HTMLInputElement).value || undefined,
    role: role || undefined,
  };

  // 자식 요소들 처리
  const children: ElementNode[] = [];
  const childElements = nodeListToArray(element.children);

  childElements.forEach((child) => {
    const childNode = buildElementTree(child);
    if (childNode) {
      children.push(childNode);
    }
  });

  return {
    element: pageElement,
    children: children,
  };
};

/**
 * NodeList를 배열로 변환합니다
 */
export const nodeListToArray = <T extends Element>(
  nodeList: NodeListOf<T> | HTMLCollectionOf<Element>,
): T[] => {
  const array: T[] = [];
  for (let i = 0; i < nodeList.length; i++) {
    array.push(nodeList[i] as T);
  }
  return array;
};
